// Cliente del modo Shorts "entorno / scene" (la PERSONA se queda, cambia el FONDO). Enfoque
// validado (ver ADR-2026-08-17-shorts-scene-lipsync). Mismo patrón async en 2 pasos:
//   1) START → dispara el job `video_shorts_scene` (backend) y devuelve un jobId.
//   2) STATUS → se polea hasta `done` (storage path en `generadas`) o `error`.
// El backend, por cada chunk de ~9s: Omni cambia SOLO el entorno → concat + audio original →
// sync-3 (fal) corrige la boca contra el audio real → Topaz 4k mejora la calidad → downscale a
// 1080×1920. Tarda VARIOS minutos (Omni en serie + sync-3 + Topaz). La plantilla manda su prompt.
import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';
import type { ShortsResult, ShortsStatus } from './shortsStudio';

export interface ShortsSceneParams {
  projectId: string;
  /** Signed URL (descargable) del video subido por el usuario. */
  sourceUrl: string;
  /** Prompt de la plantilla (define el entorno; ej. estudio de radio). */
  templatePrompt: string;
  /** Nombre/id de la plantilla (para el título del historial; ej "radio"). */
  template?: string;
  /** 9:16 por defecto (vertical). */
  aspectRatio?: string;
  /** Duración del video en segundos (define el costo). */
  durationSec: number;
}

const BUCKET = 'generadas'; // el resultado vive en el bucket del historial (generadas/{company}/full)
const POLL_INTERVAL_MS = 4000;
// Pipeline lento (Omni en serie + sync-3 ~minutos + Topaz 4k ~minutos) y si hay varios jobs
// encolados se SERIALIZAN (cola video). Con la cola llena, 30 min se quedaban cortos → el cliente
// abandonaba un job que el backend TERMINABA igual. Margen amplio: 900 × 4s = 60 min.
const POLL_MAX_ATTEMPTS = 900;

// Costo del modo scene: 4 créditos por segundo (espejo de `SHORTS_SCENE_CREDITS_PER_SEC` del
// backend — Omni + sync-3 + Topaz 4k). PLACEHOLDER de MVP, ajustar junto con el backend.
const SCENE_CREDITS_PER_SEC = 4;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/** Costo en créditos del modo scene: 4 por segundo, mínimo 1 (espejo de `shortsSceneCredits`). */
export function creditsForShortScene(durationSec: number): number {
  return Math.max(1, Math.ceil(durationSec * SCENE_CREDITS_PER_SEC));
}

/**
 * Dispara la generación del short "scene" y polea hasta terminar. Devuelve el path + signed URL.
 * Lanza Error si falla o hace timeout. `onStatus` reporta transiciones.
 */
export async function generarShortScene(
  params: ShortsSceneParams,
  onStatus?: (s: ShortsStatus) => void,
): Promise<ShortsResult> {
  const startBody = {
    project_id: params.projectId,
    source_url: params.sourceUrl,
    template_prompt: params.templatePrompt,
    template: params.template,
    aspect_ratio: params.aspectRatio ?? '9:16',
    duration_sec: params.durationSec,
  };

  // ── 1) START ──────────────────────────────────────────────────────────
  const startRes = await atlasBackendFetch('/v1/videos/shorts-scene/start', startBody);
  if (!startRes.ok) {
    throw new Error(`No se pudo iniciar la generación (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: { response?: string; jobId?: string; error?: string };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor.');
  }
  if (startJson.response !== 'success' || !startJson.jobId) {
    throw new Error(startJson.error || 'No se pudo iniciar la generación.');
  }
  const jobId = startJson.jobId;
  onStatus?.('processing');

  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; error?: string };
    try {
      const stRes = await atlasBackendFetch('/v1/videos/shorts-scene/status', { job_id: jobId });
      if (!stRes.ok) continue;
      st = await stRes.json();
    } catch {
      continue;
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Short generado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { jobId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.error || 'La generación del short falló.');
    }
  }

  // No invitamos a reintentar: el backend puede seguir procesando y, al terminar, el short queda
  // guardado y aparece en el HISTORIAL igual (el handler hace insertGeneratedImage). Reintentar acá
  // dispararía OTRO job y OTRO cobro por el mismo video. Le decimos que lo busque en el historial.
  throw new Error('La generación está tardando más de lo normal. No la reintentes: cuando termine, el short va a aparecer en tu Historial.');
}
